import { useState } from "react"
import { useNavigate } from "react-router"
import { Sale } from "../types"
import { createBid, BidsApiError } from "../services/bidsApi"
import { ApiBidRequest } from "../types/api"

interface LumpSumBidFormProps {
  sale: Sale
}

export const LumpSumBidForm = ({ sale }: LumpSumBidFormProps) => {
  const navigate = useNavigate()
  const [bidAmount, setBidAmount] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!bidAmount || parseFloat(bidAmount) <= 0) {
      setError("Please fill in all required fields")
      return
    }

    setIsSubmitting(true)
    setError(null)
    setSuccess(null)

    try {
      const bidData: ApiBidRequest = {
        bid_type: "lump sum",
        total_amount: bidAmount,
      }

      await createBid(sale.id, bidData)
      setSuccess("Bid submitted successfully!")

      // Navigate back to sale detail page after a short delay
      setTimeout(() => {
        navigate(`/birddog/sale/${sale.id}`)
      }, 2000)
    } catch (err) {
      if (err instanceof BidsApiError) {
        setError(err.message)
      } else if (err instanceof Error) {
        setError(`Failed to submit bid: ${err.message}`)
      } else {
        setError("An unexpected error occurred")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Inventory Details */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-3">
          Inventory Details
        </h3>
        <div className="overflow-x-auto">
          <table className="table-auto divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product Type
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estimated Volume
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sale.timber_products.map((product, index) => (
                <tr key={index}>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    {product.name}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                    {product.estimated_volume.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                    {product.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bid Amount */}
      <div>
        <label
          htmlFor="bid-amount"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Total Bid Amount
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500">$</span>
          </div>
          <input
            type="number"
            id="bid-amount"
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
            className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Enter your total bid amount for all timber products
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Display */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Success</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>{success}</p>
                <p>Redirecting to sale details...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div>
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={
            isSubmitting ||
            !!success ||
            !bidAmount ||
            parseFloat(bidAmount) <= 0
          }
          className={`w-full font-medium py-2 px-4 rounded-md transition-colors ${
            isSubmitting ||
            !!success ||
            !bidAmount ||
            parseFloat(bidAmount) <= 0
              ? "bg-gray-400 text-gray-600 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 text-white"
          }`}
        >
          {isSubmitting
            ? "Submitting..."
            : success
              ? "Bid Submitted"
              : "Submit Bid"}
        </button>
      </div>
    </div>
  )
}
